import type { FC, ReactNode } from 'react'
import type { Faq } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { FaqList } from '@/components/generals/faq-list'
import { StatGrid } from '@/components/generals/stat-grid'
import { VoiceReel } from '@/components/generals/voice-reel'
import { ClosingCta } from '@/components/generals/closing-cta'
import { ReasonTriad } from '@/components/generals/reason-triad'
import { ProductHero } from '@/components/generals/product-hero'
import { FeatureChapters } from '@/components/generals/feature-chapters'
import { KalkHeroVisual } from '@/components/landing/kalkulasjon/hero-visual'
import { MarginCalculator } from '@/components/landing/kalkulasjon/margin-calculator'
import { CompareTable } from '@/components/landing/kalkulasjon/compare-table'
import { FieldVideo } from '@/components/landing/kalkulasjon/field-video'
import { UI } from '@/constants/media'
import { VOICES } from '@/constants/testimonials'

const FAQ: Faq[] = [
    {
        question: 'Stemmer byggemalene overens med min måte å bygge på?',
        answer: [
            "Malene våre bygger på oppdaterte bransjestandarder og ekte akkorddata. Siden alle jobber ulikt, kan du enkelt justere tidsbruk, timepriser og påslag, slik at kalkylen alltid stemmer med din virkelighet."
        ]
    },
    {
        question: 'Hvorfor velge Svenn fremfor andre kalkulasjonsprogrammer?',
        answer: [
            "Mange tradisjonelle systemer er overkompliserte, utdaterte og krever kurs bare for å komme i gang.",
            "Svenn Kalkulasjon er bygget spesifikt for små og mellomstore bedrifter. Det er kursfritt, strippet for unødvendige steg, og lar deg lage proffe tilbud på en brøkdel av tiden."
        ],
        link: { label: 'Les sammenligningen av fem kalkulasjonsverktøy', href: '/blogg/kalkulasjonsverktoy-bygg-sammenligning' }
    },
    {
        question: 'Må jeg oppdatere materialprisene manuelt?',
        answer: [
            "Nei. Du laster enkelt inn dine egne prislister direkte fra din leverandør. Da regner du alltid med riktige innkjøpspriser, og får ut en ferdig materialliste klar til bestilling."
        ]
    },
    {
        question: 'Er 14 dager nok til å teste, og hva koster det etterpå?',
        answer: [
            "Du er i gang på to minutter, så du rekker fint å prise mange prosjekter. Prøveperioden er helt uforpliktende og krever ikke kredittkort.",
            "Ønsker du å fortsette, koster Svenn Kalkulasjon 1 599 kr i måneden for hele bedriften, uansett antall brukere."
        ],
        link: { label: 'Se alle priser', href: '/priser' }
    }
]

const Kalkulasjon: FC = (): ReactNode => {
    return (
        <Layout header='dark'>
            <Seo
                title='Kalkulasjon'
                description="Svenn Kalkulasjon: kursfritt kalkulasjonsverktøy for håndverkere. Kalkuler med egne innkjøpspriser og byggemaler, og send et proft pristilbud på 15 minutter."
            />

            <ProductHero
                eyebrow='Svenn Kalkulasjon · Kursfritt kalkulasjonsverktøy'
                titleA='Kalkuler raskere.'
                titleB='Bygg overskudd.'
                text='Priser du på erfaring og magefølelse, forsvinner marginen lett. Med Svenn kalkulerer du raskt og riktig, vinner flere jobber og beholder mer av det du tjener.'
                visual={<KalkHeroVisual />}
                points={['Ingen kredittkort kreves', 'I gang på 2 minutter', 'Ingen «auto-abonnement»']}
            />

            <ReasonTriad
                index='01'
                eyebrow='Derfor bytter håndverkere til Svenn'
                title='Et tilbud du kan stå for. Uten å bruke kvelden på det.'
                intro='Kalkulasjon handler ikke bare om å regne. Det handler om å få ut et tilbud du kan stå for.'
                reasons={[
                    { title: 'Enkelt fra første dag', text: 'Ryddig og oversiktlig, uten kronglete menyer og unødvendige steg.' },
                    { title: 'Full kontroll på marginen', text: 'Juster påslag og dekningsgrad med et enkelt tastetrykk.' },
                    { title: 'Bytt uten å betale dobbelt', text: 'Fri bruk ut oppsigelsestiden hos dagens leverandør.' }
                ]}
            />

            <FeatureChapters
                index='02'
                tone='mist'
                eyebrow='Bygg overskudd fra første spiker'
                title='Bunnsolid kalkulasjon på en brøkdel av tiden.'
                intro='Fra innkjøpspris til ferdig tilbud, med marginen synlig hele veien.'
                chapters={[
                    {
                        label: 'Prislister',
                        title: 'Kalkuler med riktige innkjøpspriser.',
                        text: "Last opp prislistene fra dine egne leverandører. Da kalkulerer du alltid med ekte innkjøpspriser, ikke utdaterte tall som spiser av marginene.",
                        points: [
                            { strong: 'Dine egne avtalepriser', after: 'rett inn i kalkylen' },
                            { before: 'Slipp å', strong: 'gjette på materialkost', after: 'midt i prisingen' },
                            { strong: 'Oppdater listen', after: 'når leverandøren endrer priser' }
                        ],
                        image: UI.prislister,
                        imageAlt: 'Import av leverandørens prisliste i Svenn Kalkulasjon'
                    },
                    {
                        label: 'Byggemaler',
                        title: 'Kalkulér et helt bygg på minutter, ikke timer.',
                        text: "Inkludert i Svenn får du et komplett bibliotek med ferdige maler bygget på erfaring fra byggmestere og ekte akkorddata.",
                        points: [
                            { before: 'Hundrevis av maler for både', strong: 'rehab, nybygg og malerjobber' },
                            { before: 'Bygget på', strong: 'oppdaterte bransjestandarder', after: 'og ekte akkorddata' },
                            { before: 'Enkelt å justere', strong: 'tidsbruk, timepriser og påslag' }
                        ],
                        image: UI.byggemaler,
                        imageAlt: 'Bibliotek med bygningselementer i Svenn Kalkulasjon'
                    },
                    {
                        label: 'Pristilbud',
                        title: 'Proffe og ryddige tilbud som imponerer.',
                        text: "Med kun et par klikk tilpasser du innholdet og gjør kalkylen om til et profesjonelt, oversiktlig pristilbud som skiller deg ut fra konkurrentene.",
                        points: [
                            { before: 'Profesjonell utforming med', strong: 'din logo og dine betingelser' },
                            { strong: 'Last ned som PDF', after: 'og send direkte til kunden' },
                            { before: 'Genererer også en', strong: 'komplett materialliste', after: 'som kan sendes til byggevarehuset' }
                        ],
                        image: UI.pristilbudPdf,
                        imageAlt: 'Ferdig pristilbud som PDF fra Svenn Kalkulasjon'
                    },
                    {
                        label: 'Tilbudsoversikt',
                        title: 'Ingen jobber tapt til glemmeboka.',
                        text: "Alle kalkylene og tilbudene dine ligger samlet på ett sted, med både status og utløpsdato. Da ser du med én gang hvilke som er sendt uten svar, og rekker å ta en telefon før tilbudet går ut på dato.",
                        points: [
                            { strong: 'Utløpsdato', after: 'på hvert tilbud, så du følger opp før det går for lang tid' },
                            { before: 'Filtrer på', strong: 'kunde, prosjekt og status', after: 'i stedet for å lete i e-posten' },
                            { before: 'Enklere å justere tilbud og sende nytt', strong: 'i løpet av minutter' }
                        ],
                        image: UI.tilbudsoversikt,
                        imageAlt: 'Oversikt over kalkyler og tilbud med status i Svenn'
                    }
                ]}
            />

            <StatGrid
                index='04'
                tone='night'
                eyebrow='Resultatene'
                title='Svenn-håndverkere tjener mer.'
                intro='De setter riktig pris, vinner flere jobber og bygger overskudd fra hvert prosjekt.'
                stats={[
                    {
                        label: 'Tid per tilbud',
                        value: '15',
                        unit: 'min',
                        text: 'fra kalkyle til et ferdig, nedlastet pristilbud. Det samme tar 2–3 timer i Excel og Word.',
                        note: 'Snitt fra Svenns kundedata.'
                    },
                    {
                        label: 'Vinnerrate',
                        value: '50',
                        unit: '%',
                        text: 'av tilbudene laget i Svenn Kalkulasjon blir vunnet.',
                        note: 'Bransjesnittet ligger rundt 30 %. Basert på Svenns kundedata.'
                    },
                    {
                        label: 'Bedre margin',
                        value: '10–15',
                        unit: '%',
                        text: 'mer lønnsomme tilbud enn med Excel og erfaringstall i hodet.',
                        note: 'Tilbud laget i Svenn Kalkulasjon mot manuell prising.'
                    }
                ]}
            />

            <MarginCalculator />
            <CompareTable />
            <FieldVideo />

            <VoiceReel
                index='08'
                tone='paper'
                title='Ekte resultater. Ekte håndverkere.'
                intro='Hør fra noen av de 1000+ bedriftene som har byttet til Svenn Kalkulasjon.'
                items={[VOICES.kenneth, VOICES.tom, VOICES.perKalk, VOICES.tomPris, VOICES.georg]}
            />

            <FaqList items={FAQ} index='09' />

            <ClosingCta
                lines={['Slutt å la penger', 'ligge på bordet.']}
                text='Prøv Svenn Kalkulasjon gratis og uforpliktende i 14 dager. Bytter du fra et annet system, får du i tillegg fri bruk ut oppsigelsestiden din, så du slipper å betale dobbelt.'
                note='Deretter 1 599 kr i måneden for hele bedriften, uansett antall brukere. Pris eks. mva.'
                points={['14 dager helt gratis', 'Krever ikke kredittkort', 'Ingen auto-abonnement', 'Fri bruk ut oppsigelsestiden hvis du bytter']}
            />
        </Layout>
    )
}

export default Kalkulasjon
